import React from 'react'
import { motion } from 'framer-motion'
import { useApp } from '../contexts/AppContext'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiInfo, FiCheck, FiExternalLink, FiDatabase, FiSearch, FiFilter, FiTrendingUp } = FiIcons

const AboutSection = () => {
  const { currentLang, translations } = useApp()

  const features = [
    translations.feature1[currentLang],
    translations.feature2[currentLang],
    translations.feature3[currentLang],
    translations.feature4[currentLang]
  ]

  // 內部連結資料 - 增強 SEO
  const internalLinks = [
    {
      href: "#search-section",
      icon: FiSearch,
      text: currentLang === 'zh' ? '搜尋銀行代碼' : 'Search Bank Codes',
      ariaLabel: currentLang === 'zh' ? '跳轉至搜尋區域' : 'Jump to search section'
    },
    {
      href: "#results-section",
      icon: FiDatabase,
      text: currentLang === 'zh' ? '瀏覽127間銀行' : 'Browse 127 Banks',
      ariaLabel: currentLang === 'zh' ? '跳轉至結果區域' : 'Jump to results section'
    },
    {
      href: "#quick-navigation",
      icon: FiFilter,
      text: currentLang === 'zh' ? '快速篩選範圍' : 'Quick Filter Ranges',
      ariaLabel: currentLang === 'zh' ? '跳轉至快速導航' : 'Jump to quick navigation'
    }
  ]

  // 代表性銀行範例 - 提升 AI 搜尋理解
  const representativeBanks = [
    { code: '004', nameEn: 'HSBC', nameZh: '滙豐銀行' },
    { code: '024', nameEn: 'Hang Seng Bank', nameZh: '恒生銀行' },
    { code: '003', nameEn: 'Standard Chartered', nameZh: '渣打銀行' },
    { code: '012', nameEn: 'Bank of China (Hong Kong)', nameZh: '中國銀行(香港)' },
    { code: '387', nameEn: 'ZA Bank', nameZh: 'ZA Bank' },
    { code: '388', nameEn: 'Livi Bank', nameZh: 'Livi Bank' }
  ]

  return (
    <motion.section
      id="about-section"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.6 }}
      className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-6 sm:p-8"
      role="region"
      aria-labelledby="about-section-title"
      itemScope
      itemType="https://schema.org/Article"
    >
      <header className="flex items-start gap-3 mb-6">
        <motion.div 
          className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-md"
          whileHover={{ scale: 1.1, rotate: 10 }}
          transition={{ duration: 0.2 }}
          aria-hidden="true"
        >
          <SafeIcon icon={FiInfo} className="w-4 h-4 text-white" />
        </motion.div>
        <div>
          <h2 id="about-section-title" className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white" itemProp="headline">
            {currentLang === 'zh' ? '甚麼是香港銀行代碼目錄？' : 'What Is the Hong Kong Bank Code Directory?'}
          </h2>
          <p
            className="mt-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400"
            aria-label={currentLang === 'zh' ? '文章發布及更新日期' : 'Article publication and update dates'}
          >
            <span>
              {currentLang === 'zh' ? '發布：' : 'Published: '}
              <time dateTime="2024-01-01" itemProp="datePublished">
                {currentLang === 'zh' ? '2024年1月1日' : 'January 1, 2024'}
              </time>
            </span>
            <span aria-hidden="true"> · </span>
            <span>
              {currentLang === 'zh' ? '更新：' : 'Updated: '}
              <time dateTime="2026-03-04" itemProp="dateModified">
                {currentLang === 'zh' ? '2026年3月4日' : 'March 4, 2026'}
              </time>
            </span>
          </p>
        </div>
      </header>

      {/* 主要功能區域 - 增強視覺效果 */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500" aria-hidden="true" />
          {currentLang === 'zh' ? '這個銀行代碼工具提供哪些功能？' : 'What Features Does This Bank Code Tool Provide?'}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" role="list">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.8 + index * 0.1 }}
              whileHover={{ scale: 1.02, x: 5 }}
              className="flex items-start gap-3 p-3 rounded-lg bg-green-50/50 dark:bg-green-900/10 border border-green-100 dark:border-green-800/30 hover:border-green-300 dark:hover:border-green-600 transition-all duration-200"
              role="listitem"
            >
              <div className="w-5 h-5 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm" aria-hidden="true">
                <SafeIcon icon={FiCheck} className="w-3 h-3 text-white" />
              </div>
              <span className="text-gray-700 dark:text-gray-300 text-sm sm:text-base leading-relaxed">{feature}</span>
            </motion.div>
          ))}
        </div>
      </div>

      {/* 內部導航連結區域 - 提升 SEO */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {currentLang === 'zh' ? '如何快速查找香港銀行代碼？' : 'How Can You Quickly Find a Hong Kong Bank Code?'}
        </h3>
        <nav className="flex flex-wrap gap-3" aria-label={currentLang === 'zh' ? '內部快速導航' : 'Internal quick navigation'}>
          {internalLinks.map((link, index) => (
            <motion.a
              key={index}
              href={link.href}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 1.0 + index * 0.1 }}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-lg border border-blue-200 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:border-blue-300 dark:hover:border-blue-600 transition-all duration-200 text-sm font-medium"
              aria-label={link.ariaLabel}
              onClick={(e) => {
                e.preventDefault()
                const target = document.querySelector(link.href)
                if (target) {
                  target.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
              }}
            >
              <SafeIcon icon={link.icon} className="w-4 h-4" aria-hidden="true" />
              {link.text}
              <SafeIcon icon={FiExternalLink} className="w-3 h-3 opacity-60" aria-hidden="true" />
            </motion.a>
          ))}
        </nav>
      </div>

      {/* 代表性銀行範例 - AI 搜尋優化 */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <SafeIcon icon={FiTrendingUp} className="w-5 h-5 text-purple-500" aria-hidden="true" />
          {currentLang === 'zh' ? '常用香港銀行的代碼是甚麼？' : 'What Are the Codes for Popular Hong Kong Banks?'}
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {representativeBanks.map((bank, index) => (
            <motion.div
              key={bank.code}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 1.3 + index * 0.05 }}
              whileHover={{ scale: 1.03, y: -2 }}
              className="p-3 rounded-lg bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/10 dark:to-blue-900/10 border border-purple-100 dark:border-purple-800/30 hover:border-purple-300 dark:hover:border-purple-600 transition-all duration-200"
              itemScope
              itemType="https://schema.org/BankOrCreditUnion"
            >
              <div className="font-mono text-lg font-bold text-purple-600 dark:text-purple-400 mb-1" itemProp="identifier">
                {bank.code}
              </div>
              <div className="text-xs text-gray-700 dark:text-gray-300 font-medium mb-0.5" itemProp="name" lang="en">
                {bank.nameEn}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400" itemProp="alternateName" lang="zh-HK">
                {bank.nameZh}
              </div>
            </motion.div>
          ))}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 italic">
          {currentLang === 'zh' 
            ? '* 包括8間虛擬銀行：ZA Bank (387)、Livi Bank (388)、MOX Bank (389)、WeLab Bank (390)、Fusion Bank (391)、PingAn OneConnect (392)、Ant Bank (393)、Airstar Bank (395)'
            : '* Includes 8 virtual banks: ZA Bank (387), Livi Bank (388), MOX Bank (389), WeLab Bank (390), Fusion Bank (391), PingAn OneConnect (392), Ant Bank (393), Airstar Bank (395)'}
        </p>
      </div>

      {/* 描述和資料來源區域 */}
      <div className="space-y-6" itemProp="articleBody">
        <motion.p 
          className="text-gray-600 dark:text-gray-400 leading-relaxed text-sm sm:text-base"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 1.6 }}
        >
          {translations.aboutDescription[currentLang]}
        </motion.p>
        
        <motion.div 
          className="text-sm text-gray-500 dark:text-gray-400 p-4 bg-gradient-to-r from-gray-50 to-blue-50/30 dark:from-gray-700/50 dark:to-blue-900/10 rounded-lg border border-gray-200 dark:border-gray-600"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 1.8 }}
        >
          <div className="flex items-start gap-2">
            <SafeIcon icon={FiDatabase} className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
            <div>
              <div className="font-medium text-gray-700 dark:text-gray-300 mb-1">
                {currentLang === 'zh' ? '資料來源' : 'Data Source'}
              </div>
              <div itemProp="sourceOrganization" itemScope itemType="https://schema.org/Organization">
                <span itemProp="name">{translations.dataSource[currentLang]}</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.section>
  )
}

export default AboutSection